(function () {
  const { Manager } = window;
  const link = window.Fandango.link;

  const DEBOUNCE_MS = 300;
  const PAGE_SIZE = 48;

  const ENDPOINTS = {
    comingSoon: () => link.napiMwebComingSoon(),
    nowPlaying: () => link.napiMoviesInTheaters()
  };

  class MoviesDiscoveryManager extends Manager {
    constructor() {
      super();
      this.enforceActions = true;
      this.traceName = 'MoviesDiscoveryManager';

      this.state = {
        action: 'constructor',
        sectionName: '',
        url: '',
        filterState: { genre: null, mpaaRating: null },
        page: 2,
        pageSize: PAGE_SIZE,
        isFetching: false,
        domEvent: {},
        domEventCount: 0,
        errors: []
      };

      this._debounceTimer = null;
      this._abortController = null;
    }

    connectedCallback() {
      super.connectedCallback();

      const sectionName = this.getAttribute('section-name') || '';
      const targetSelector = this.getAttribute('target') || '.js-grid-container';
      const endpointFn = ENDPOINTS[sectionName];
      const url = endpointFn ? endpointFn() : '';

      const container = document.querySelector(targetSelector);
      if (!container) {
        console.error(`MoviesDiscoveryManager: target container "${targetSelector}" not found`); // eslint-disable-line no-console
        return;
      }

      this.dom = {
        filterModal: document.querySelector('filter-modal'),
        ...this.domElements(container),
        ...this.domElements(document)
      };

      this.domActions([], container);
      this.domActions([], document);

      // Cache the live offer banner node so grid re-renders can move it
      // (not recreate it) — preserves the initialized Splide carousel.
      const movieList = this.dom.movieList?.[0];
      this._offerBanner = movieList
        ? movieList.querySelector('#global-offer-banner')
        : null;

      this.setState({ action: 'init', sectionName, url });
    }

    stateUpdated() {
      switch (this.state.action) {
        case 'init': {
          this._attachCustomEvents();
          this._readUrlParams();
          this._syncModalState();
          this._updateFilterButtons();
          this._updateGenreButtons();
          if (window.Fandango.util && window.Fandango.util.lazyLoad) {
            window.Fandango.util.lazyLoad();
          }
          break;
        }

        case 'onLoadMoreBtnClick': {
          this.state.domEvent.preventDefault();
          if (!this.state.isFetching) {
            this.setState({ action: 'loadMore', isFetching: true });
          }
          break;
        }

        case 'onFilterBtnClick': {
          this.state.domEvent.preventDefault();
          if (this.dom.filterModal && typeof this.dom.filterModal.show === 'function') {
            this.dom.filterModal.show(this.state.domEvent.currentTarget);
          }
          break;
        }

        case 'onNoResultsBtnClick': {
          this.state.domEvent.preventDefault();
          this._clearFilters();
          break;
        }

        case 'onGenreBtnClick': {
          this.state.domEvent.preventDefault();
          const genre = this.state.domEvent.currentTarget.dataset.genre;
          const filterState = { ...this.state.filterState, genre };
          this.setState({ action: 'genreNavChanged', filterState, page: 2 });
          break;
        }

        case 'onClearGenreBtnClick': {
          this.state.domEvent.preventDefault();
          const filterState = { ...this.state.filterState, genre: null };
          this.setState({ action: 'genreNavChanged', filterState, page: 2 });
          break;
        }

        case 'genreNavChanged': {
          this._syncModalState();
          this.setState({ action: 'filterChanged' });
          break;
        }

        case 'filterChanged':
        case 'filterCleared': {
          this._updateFilterButtons();
          this._updateGenreButtons();
          this._fetchMoviesDebounced();
          break;
        }

        case 'fetchMovies': {
          this._showPlaceholders();
          this._fetchMovies();
          break;
        }

        case 'fetchMoviesSuccess': {
          this._renderMovies(this.state._fetchedMovies, 'replace');
          this.setState({ action: 'idle', _fetchedMovies: null });
          break;
        }

        case 'fetchMoviesFail': {
          this.setState({ action: 'idle' });
          break;
        }

        case 'loadMore': {
          this._fetchNextPage();
          break;
        }

        case 'fetchNextPageSuccess': {
          this._renderMovies(this.state._fetchedMovies, 'append');
          this.setState({
            action: 'idle',
            _fetchedMovies: null,
            page: this.state.page + 1
          });
          this._syncUrlParams();
          break;
        }

        case 'fetchNextPageFail': {
          this._hideLoadMore();
          this.setState({ action: 'idle' });
          break;
        }

        case 'idle':
          break;
      }
    }

    // --- Event Handlers ---

    _onFilterGroupChange(e) {
      const { group, value } = e.detail;
      const filterState = { ...this.state.filterState };

      if (group === 'Genres') {
        filterState.genre = value;
      } else if (group === 'MPA Ratings') {
        filterState.mpaaRating = value;
      }

      this.setState({ action: 'filterChanged', filterState, page: 2 });
    }

    _onFilterClear() {
      this._clearFilters();
    }

    // --- Private Methods ---

    _attachCustomEvents() {
      const { filterModal } = this.dom;

      if (filterModal) {
        this.attachEvent(filterModal, 'filter-group-change', this._onFilterGroupChange);
        this.attachEvent(filterModal, 'filter-modal-clear', this._onFilterClear);
      }
    }

    _clearFilters() {
      const filterState = { genre: null, mpaaRating: null };

      if (this.dom.filterModal) {
        const groups = this.dom.filterModal.querySelectorAll('filter-group');
        groups.forEach(group => group.clearSelection());
      }

      this.setState({ action: 'filterCleared', filterState, page: 2 });
    }

    _readUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const filterState = { ...this.state.filterState };

      if (params.get('GenreFilter')) {
        filterState.genre = decodeURIComponent(params.get('GenreFilter'));
      }
      if (params.get('RatingFilter')) {
        filterState.mpaaRating = decodeURIComponent(params.get('RatingFilter'));
      }

      if (filterState.genre || filterState.mpaaRating) {
        this.state.filterState = filterState;
      }

      // Read page param - represents current visible page (1-based)
      if (params.get('page')) {
        const pageParam = parseInt(params.get('page'), 10);
        if (pageParam > 1 && !Number.isNaN(pageParam)) {
          // State page represents "next page to fetch", so add 1
          this.state.page = pageParam + 1;
        }
      }
    }

    _syncUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const { genre, mpaaRating } = this.state.filterState;

      if (genre) {
        params.set('GenreFilter', genre);
      } else {
        params.delete('GenreFilter');
      }

      if (mpaaRating) {
        params.set('RatingFilter', mpaaRating);
      } else {
        params.delete('RatingFilter');
      }

      // Add page param only when > 1
      // State page represents "next page to fetch", so current page is page - 1
      const currentPage = this.state.page - 1;
      if (currentPage > 1) {
        params.set('page', currentPage);
      } else {
        params.delete('page');
      }

      const qs = params.toString();
      const newUrl = window.location.pathname + (qs ? '?' + qs : '');
      window.history.replaceState(null, '', newUrl);
    }

    _syncModalState() {
      const { filterModal } = this.dom;
      if (!filterModal) {
        return;
      }

      const { genre, mpaaRating } = this.state.filterState;
      const groups = filterModal.querySelectorAll('filter-group');

      groups.forEach((group) => {
        const groupLabel = group.getAttribute('label');

        if (groupLabel === 'Genres') {
          group.clearSelection();
          if (genre) {
            const facet = group.querySelector(`filter-facet[value="${genre}"]`);
            if (facet) {
              facet.setAttribute('selected', '');
            }
          }
        } else if (groupLabel === 'MPA Ratings') {
          group.clearSelection();
          if (mpaaRating) {
            const facet = group.querySelector(`filter-facet[value="${mpaaRating}"]`);
            if (facet) {
              facet.setAttribute('selected', '');
            }
          }
        }
      });
    }

    _updateFilterButtons() {
      const { genre, mpaaRating } = this.state.filterState;
      const activeCount = [genre, mpaaRating].filter(Boolean).length;

      if (this.dom.filterBtn) {
        this.dom.filterBtn.forEach((btn) => {
          const countEl = btn.querySelector('.count');
          if (countEl) {
            countEl.textContent = activeCount || '';
          }
          btn.classList.toggle('active', activeCount > 0);
        });
      }
    }

    _updateGenreButtons() {
      const { genre } = this.state.filterState;

      if (this.dom.genreBtn) {
        this.dom.genreBtn.forEach((btn) => {
          const btnGenre = btn.dataset.genre;
          const isSelected = btnGenre === genre;
          btn.classList.toggle('selected', isSelected);
          if (isSelected) {
            btn.setAttribute('aria-current', 'true');
          } else {
            btn.removeAttribute('aria-current');
          }
        });
      }

      if (this.dom.clearGenreBtn) {
        this.dom.clearGenreBtn.forEach((btn) => {
          if (genre) {
            btn.classList.remove('fd-hide');
          } else {
            btn.classList.add('fd-hide');
          }
        });
      }
    }

    _fetchMoviesDebounced() {
      if (this._debounceTimer) {
        clearTimeout(this._debounceTimer);
      }

      this._debounceTimer = setTimeout(() => {
        this.setState({ action: 'fetchMovies', isFetching: true });
      }, DEBOUNCE_MS);
    }

    async _fetchMovies() {
      if (this._abortController) {
        this._abortController.abort();
      }
      this._abortController = new AbortController();

      const { url, filterState, pageSize } = this.state;
      const params = new URLSearchParams({ raw: true, page: 1, pageSize });

      if (filterState.genre) {
        params.set('genre', filterState.genre);
      }
      if (filterState.mpaaRating) {
        params.set('mpaaRating', filterState.mpaaRating);
      }

      try {
        const response = await fetch(`${url}?${params}`, {
          signal: this._abortController.signal,
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const movies = await response.json();
        this._syncUrlParams();
        this.setState({ action: 'fetchMoviesSuccess', isFetching: false, _fetchedMovies: movies, page: 2 });
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        this.setState({ action: 'fetchMoviesFail', isFetching: false });
      }
    }

    async _fetchNextPage() {
      const { url, page, pageSize, filterState } = this.state;
      const params = new URLSearchParams({ raw: true, page, pageSize });

      if (filterState.genre) {
        params.set('genre', filterState.genre);
      }
      if (filterState.mpaaRating) {
        params.set('mpaaRating', filterState.mpaaRating);
      }

      const loadMoreBtn = this.dom.loadMoreBtn?.[0];
      const loading = this.dom.loading?.[0];
      const placeholderContainer = this.dom.placeholderContainer?.[0];

      if (loadMoreBtn) {
        loadMoreBtn.classList.add('fd-hide');
      }
      if (loading) {
        loading.classList.remove('fd-hide');
      }
      if (placeholderContainer) {
        placeholderContainer.classList.remove('fd-hide');
      }

      try {
        const response = await fetch(`${url}?${params}`, {
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const movies = await response.json();
        this.setState({ action: 'fetchNextPageSuccess', isFetching: false, _fetchedMovies: movies });
      } catch (err) {
        this.setState({ action: 'fetchNextPageFail', isFetching: false });
      }
    }

    _renderMovies(movies, mode) {
      const movieList = this.dom.movieList?.[0];
      const gridActions = this.dom.gridActions?.[0];
      const noResultsContainer = this.dom.noResultsContainer?.[0];
      const pageTitle = this.dom.pageTitle?.[0];
      const loadMoreBtn = this.dom.loadMoreBtn?.[0];
      const loading = this.dom.loading?.[0];
      const placeholderContainer = this.dom.placeholderContainer?.[0];

      if (!movieList) {
        return;
      }

      const featureFlags = window.Fandango?.featureFlags || {};
      const streamingEnabled = window.Fandango?.streamingEnabled || false;
      const showBanners = window.Fandango?.showBanners || false;

      if (loading) {
        loading.classList.add('fd-hide');
      }
      if (placeholderContainer) {
        placeholderContainer.classList.add('fd-hide');
      }

      if (!movies || !movies.length) {
        if (mode === 'replace') {
          movieList.innerHTML = '';
          if (noResultsContainer) {
            noResultsContainer.classList.remove('fd-hide');
          }
          if (gridActions) {
            gridActions.classList.add('fd-hide');
          }
          if (pageTitle) {
            pageTitle.classList.add('fd-hide');
          }
          this._announceResults(0);
        } else {
          this._hideLoadMore();
        }
        return;
      }

      if (noResultsContainer) {
        noResultsContainer.classList.add('fd-hide');
      }
      if (pageTitle) {
        pageTitle.classList.remove('fd-hide');
      }

      const template = window.Fandango.templates._moviesGridItem;
      const markup = movies.map(movie =>
        window.swig.run(template, { movie, featureFlags, streamingEnabled, showBanners })
      ).join('');

      if (mode === 'replace') {
        // Detach the live banner node before wiping, then move it back so
        // it stays last in the grid without re-initializing its carousel.
        if (this._offerBanner) {
          this._offerBanner.remove();
        }
        movieList.innerHTML = markup;
        if (this._offerBanner) {
          movieList.appendChild(this._offerBanner);
        }
      } else if (this._offerBanner && this._offerBanner.parentNode === movieList) {
        // Insert appended items before the banner so it remains last.
        this._offerBanner.insertAdjacentHTML('beforebegin', markup);
      } else {
        movieList.insertAdjacentHTML('beforeend', markup);
      }

      if (movies.length < this.state.pageSize) {
        this._hideLoadMore();
      } else if (gridActions) {
        gridActions.classList.remove('fd-hide');
        if (loadMoreBtn) {
          loadMoreBtn.classList.remove('fd-hide');
        }
      }

      if (mode === 'replace') {
        this._announceResults(movies.length);
      }

      if (!movieList.classList.contains('fd-lazy-container')) {
        movieList.classList.add('fd-lazy-container');
      }
      if (window.Fandango.util && window.Fandango.util.lazyLoad) {
        window.Fandango.util.lazyLoad();
      }
    }

    _hideLoadMore() {
      const loadMoreBtn = this.dom.loadMoreBtn?.[0];
      const gridActions = this.dom.gridActions?.[0];
      if (loadMoreBtn) {
        loadMoreBtn.classList.add('fd-hide');
      }
      if (gridActions) {
        gridActions.classList.add('fd-hide');
      }
    }

    _announceResults(count) {
      const resultsLive = this.dom.resultsLive?.[0];
      if (resultsLive) {
        resultsLive.textContent = count
          ? `Showing ${count} movie${count !== 1 ? 's' : ''}`
          : 'No movies match your filters';
      }
    }

    _showPlaceholders() {
      const movieList = this.dom.movieList?.[0];
      const placeholderContainer = this.dom.placeholderContainer?.[0];
      const noResultsContainer = this.dom.noResultsContainer?.[0];
      const pageTitle = this.dom.pageTitle?.[0];

      if (movieList) {
        // Preserve the live banner node across the placeholder wipe; it is
        // re-appended on the next successful replace render.
        if (this._offerBanner) {
          this._offerBanner.remove();
        }
        movieList.innerHTML = '';
      }
      if (placeholderContainer) {
        placeholderContainer.classList.remove('fd-hide');
      }
      if (noResultsContainer) {
        noResultsContainer.classList.add('fd-hide');
      }
      if (pageTitle) {
        pageTitle.classList.remove('fd-hide');
      }
    }
  }

  if (!window.customElements.get('movies-discovery-manager')) {
    window.customElements.define('movies-discovery-manager', MoviesDiscoveryManager);
  }

  /* istanbul ignore next */
  if (typeof module !== 'undefined') {
    module.exports = MoviesDiscoveryManager;
  }
})();
